import React, { useState } from "react";
import {
  _goToNote,
  _goToFactor,
  _goToReminder,
  _goToCustomer,
} from "routers/helper";

import { View, StyleSheet, TouchableOpacity, Platform } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Button from "components/Button";
import Accordion from "components/Accordion";

import { mq, dial, sendSms, alertPermission, sn } from "utils";
import { sizes, colors, strings } from "assets/constants";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import css from "styles";
import { useChmod, useFlag } from "hooks";
import tinycolor from "tinycolor2";
import Modal from "./Modal";
import GridView from "./GridView";
import { useEditCustomer } from "clients/apis";

const iconSize = sn(22, 25, 25, 25);

const City = (props: { city?: string; color: string }) => {
  const { city, color } = props;
  return (
    <View style={css.rRowCenterAlign}>
      <Icon color={color} size={12} name="map-pin" />
      <Text
        weight="bold"
        size={"sm"}
        color={colors.text.gray}
        style={{ marginRight: mq(4) }}
      >
        {strings.city}:{" "}
      </Text>

      <Text
        size={"sm"}
        color={city ? colors.text.normal : colors.text.muted}
        weight="bold"
      >
        {city || "ثبت نشده"}
      </Text>
    </View>
  );
};

const CustomerInfo = (props: {
  expanded?: boolean;
  mode: "simple" | "collapsible";
  customer: CustomerT;
}) => {
  const { role } = useRecoilValue(authAtom);
  const { expanded, mode, customer } = props;

  const color = colors.theme;

  // const showFooter = role === "cto" && customer?.seller;

  const styles = StyleSheet.create({
    container: {
      minHeight: 50,
      padding: sizes.space,
      borderRadius: sizes.radius.xl,
      backgroundColor: colors.itemsBg,
    },
  });

  return (
    <View style={styles.container}>
      {/** header */}
      <View
        style={{ ...css.rRowCenterAlignSpaceBetween, marginBottom: mq(10) }}
      >
        <View style={css.rRowCenterAlign}>
          <Text weight="extraBold" size={"lg"}>
            {customer?.name?.trim()}
          </Text>

          <View style={{ top: 2, marginRight: 5 }}>
            <Icon name={"chevron-left"} size={12} />
          </View>

          <Text
            size={"sm"}
            weight="bold"
            style={{ bottom: 1 }}
            color={colors.text.gray}
          >
            {customer?.status?.title?.trim()}
          </Text>
        </View>

        <View style={{ flexDirection: "row" }}>
          <Text
            weight="extraBold"
            color={tinycolor(colors.theme).darken(10).toString()}
          >
            {customer?.id}
          </Text>
          <Text color={colors.text.gray}>کد: </Text>
        </View>

        <View
          style={{
            width: 22,
            height: 22,
            backgroundColor: colors.white,
            borderRadius: 22,
            position: "absolute",
            top: -20,
            ...Platform.select({
              web: {
                left: -15,
              },
              android: {
                right: -15,
              },
            }),
            alignItems: "center",
            justifyContent: "center",
            ...css.shadow(5),
          }}
        >
          <Icon
            name={
              mode === "simple"
                ? "chevron-left"
                : expanded
                ? "chevron-up"
                : "chevron-down"
            }
          />
        </View>
      </View>

      {/** center */}
      <View style={css.rRowCenterAlignSpaceBetween}>
        <Icon color={color} size={12} name="smartphone" />
        <Text
          size={"xs"}
          weight="extraBold"
          color={colors.text.gray}
          style={{ letterSpacing: 0.8, marginRight: sizes.iconTextSpace }}
        >
          {customer?.mobile}
        </Text>
        <View style={css.container} />

        <City city={customer?.cityName} color={color} />
      </View>
    </View>
  );
};

const Collapsible = (props: {
  customer: CustomerT;
  expanded: boolean;
  onPress?: () => void;
  onLongPress?: () => void;
  extraData?: any;
}) => {
  const { customer, extraData } = props;

  const [customerCat, setCustomerCat] = useState(customer?.category?.id);
  const _onCloseModal = () => {
    setCustomerCat(customer?.category?.id);
    hideModal();
  };

  const editCustomerMut = useEditCustomer();
  const _onUpdate = () => {
    editCustomerMut.mutate(
      {
        id: customer?.id,
        data: {
          section: "basic",
          name: customer?.name,
          mobile: customer?.mobile,
          category_id: customerCat,
        },
      },
      { onSuccess: hideModal }
    );
  };

  const { flg: modal, on: showModal, off: hideModal } = useFlag();

  const { chmod } = useChmod();

  const _onDetails = () => _goToCustomer({ data: customer });
  const _onNote = () =>
    chmod("note", "POST") ? _goToNote({ customer }) : alertPermission();
  const _onFactor = () =>
    chmod("factor", "POST") ? _goToFactor({ customer }) : alertPermission();
  const _onReminder = () =>
    chmod("reminder", "POST") ? _goToReminder({ customer }) : alertPermission();
  const _onSms = () => customer?.mobile && sendSms(customer?.mobile);
  const _onDial = () => customer?.mobile && dial(customer?.mobile);

  const renderAccordionHeader = (expanded: boolean) => (
    <CustomerInfo expanded={expanded} customer={customer} mode="collapsible" />
  );

  const renderAccordionContent = () => (
    <>
      <View style={styles.content}>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onDial}
            bgColor={colors.success}
            icon={{ name: "phone", size: iconSize }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onSms}
            bgColor={colors.info}
            icon={{ name: "mail", size: iconSize }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onReminder}
            bgColor={colors.warning}
            icon={{ name: "bell", size: iconSize, color: colors.white }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onFactor}
            bgColor={colors.cyan}
            icon={{ name: "file-text", size: iconSize, color: colors.white }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onNote}
            bgColor={colors.blue}
            icon={{ name: "clipboard", size: iconSize }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={showModal}
            bgColor={colors.purple}
            icon={{ name: "repeat", size: iconSize }}
            circle="100%"
          />
        </View>
        <View style={styles.contentItemContainer}>
          <Button
            onPress={_onDetails}
            bgColor={colors.screensBg}
            icon={{ name: "more-vertical", size: iconSize }}
            circle="100%"
          />
        </View>
      </View>

      <Modal visible={modal} close={_onCloseModal} hideCloseBtn>
        <>
          <View style={{ marginBottom: 25 }}>
            <Text weight="bold" size={"xxl"}>
              {strings.changing_customer_group}
            </Text>
          </View>
          <GridView
            spacing={sizes.space / 2}
            gridHeight={70}
            numberOfCol={sn(3, 3, 4, 5)}
            data={extraData?.categories?.data?.data}
            renderItem={(item: any) => {
              const isSelected = item?.id === customerCat;

              const _onSelect = () => setCustomerCat(item?.id);

              return (
                <TouchableOpacity
                  style={css.cover}
                  onPress={_onSelect}
                  activeOpacity={sizes.tapOpacity}
                >
                  <View
                    style={{
                      width: "100%",
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: sizes.radius.lg,
                      backgroundColor: isSelected
                        ? tinycolor(colors.theme).lighten(50).toString()
                        : colors.white,
                      paddingHorizontal: 5,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? tinycolor(colors.theme).lighten(40).toString()
                        : tinycolor(colors.text.normal).lighten(64).toString(),
                    }}
                  >
                    <Text
                      weight={isSelected ? "bold" : "default"}
                      color={isSelected ? colors.theme : colors.text.normal}
                      align="center"
                    >
                      {item?.title}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            }}
          />
        </>
        <View
          style={{
            ...css.paddingH,
            ...css.rowCenterAlign,
            marginBottom: sizes.space,
          }}
        >
          <Button
            loading={editCustomerMut?.isLoading}
            onPress={_onUpdate}
            title={{ value: strings.confirm }}
            width={mq(100)}
            bgColor={colors.success}
            style={{ marginRight: sizes.space }}
          />

          <Button
            onPress={_onCloseModal}
            title={{ value: strings.cancel }}
            outline
            width={mq(100)}
            bgColor={colors.danger}
          />
        </View>
      </Modal>
    </>
  );

  return (
    <Accordion
      style={styles.box}
      onPress={props.onPress}
      onLongPress={props.onLongPress}
      expanded={props.expanded}
      renderHeader={renderAccordionHeader}
      renderContent={renderAccordionContent}
    />
  );
};

const Simple = (props: {
  customer: CustomerT;
  onPress?: (customer: CustomerT) => void;
}) => (
  <TouchableOpacity onPress={() => props.onPress(props.customer)}>
    <CustomerInfo customer={props.customer} mode="simple" />
  </TouchableOpacity>
);

export default { Collapsible, Simple };

const styles = StyleSheet.create({
  box: { backgroundColor: colors.itemsBg, borderRadius: sizes.radius.xl },

  content: {
    width: "100%",
    paddingVertical: mq(10),
    flexDirection: "row-reverse",
    justifyContent: "center",
  },

  contentItemContainer: {
    width: sn(39, 43, 45, 50),
    aspectRatio: 1,
    borderRadius: 100,
    marginHorizontal: mq(3),
    justifyContent: "center",
    alignItems: "center",
  },
});
